/**
 * /ko/pdf-summary — PDF/문서 요약 reframe bridge (Korean).
 *
 * Targets pdf 요약 ai (1.4k, KD 0) / pdf 요약 / 문서 요약 ai. Reframe: 요약은
 * 결론, 결정은 맥락이 붙은 결론.
 */
import { MarketingShell } from './MarketingShell';

export function KoPdfSummary() {
  return (
    <MarketingShell
      currentLocale="ko"
      hreflangPath="/pdf-summary"
      hreflangLocales={['ko']}
      documentTitle="PDF 요약 AI｜긴 문서를 빠르게 요약하는 법 (+ 그다음) · Draftlize"
      documentDescription="AI로 PDF·긴 문서를 몇 초 만에 요약하는 방법과, 요약한 내용을 '의사결정'으로 연결해 활용하는 법까지 안내합니다."
    >
      <section className="hero" style={{ paddingTop: 88, paddingBottom: 64 }}>
        <div style={{ maxWidth: 820 }}>
          <span className="label label-amber">가이드 · 문서 요약</span>
          <h1 className="serif" style={{ fontSize: 'clamp(32px, 4.4vw, 54px)', lineHeight: 1.08, margin: '12px 0 24px' }}>
            PDF 요약 AI——<br />
            <em>요약 다음이 진짜 중요하다.</em>
          </h1>
          <p className="lede">
            긴 PDF를 AI에 넣으면 몇 초 만에 요약이 나옵니다. 편리하죠. 하지만 일터에서 진짜 필요한 것은 '짧은 결론'이 아니라 <em>맥락이 붙은 결정</em>입니다. 왜 그렇게 정했는지, 무엇을 버렸는지——요약은 바로 그 부분을 깎아냅니다. 이 페이지는 PDF·문서 요약의 기본과, 요약보다 한 단계 깊은 '결정을 남기는' 법을 다룹니다.
          </p>
        </div>
      </section>

      <section className="problem">
        <div className="head">
          <h2 className="serif">PDF를 AI로 요약하는 법.</h2>
        </div>
        <div style={{ maxWidth: 760 }}>
          <ol style={{ fontSize: 16, lineHeight: 2, color: 'var(--ink-1)', margin: 0, paddingLeft: 22 }}>
            <li><strong>문서 업로드</strong>——PDF, 워드, 또는 긴 텍스트.</li>
            <li><strong>요약 생성</strong>——AI 요약 도구가 핵심만 추려 줍니다(대부분 무료 한도 제공).</li>
            <li><strong>검토</strong>——숫자·고유명사가 맞는지 확인.</li>
          </ol>
          <p style={{ fontSize: 17, lineHeight: 1.8, color: 'var(--ink-1)', margin: '22px 0 0' }}>
            요약은 나왔습니다. 그런데 그 요약이 어떤 <em>결정</em>으로 이어졌나요? 회의나 리서치 음성이 원본이라면 먼저 <a href="/ko/voice-to-text" style={{ color: 'var(--ink-0)', textDecoration: 'underline' }}>음성 텍스트 변환</a> 후 요약하는 것도 같은 흐름입니다.
          </p>
        </div>
      </section>

      <section className="compare">
        <div className="head">
          <div>
            <span className="label">결론 vs 맥락이 붙은 결론</span>
            <h2 className="serif">요약은 결론. <em>결정은 이유를 데려온 결론.</em></h2>
          </div>
          <p>요약은 '무엇이 적혀 있었나'를 줄입니다. 결정 기록은 '무엇을, 왜 정했나'를 최신으로 유지합니다. 일에서 효과 있는 건 후자입니다.</p>
        </div>
        <table className="compare-table">
          <thead>
            <tr>
              <th></th>
              <th>요약</th>
              <th className="ours">결정 기록 (Draftlize)</th>
            </tr>
          </thead>
          <tbody>
            <tr><td>남는 것</td><td>결론·요점</td><td className="ours yes">결정 + 이유 + 담당자</td></tr>
            <tr><td>'왜'</td><td className="no">대개 깎여 나감</td><td className="ours yes">카드에 보존</td></tr>
            <tr><td>이전 결정을 뒤집을 때</td><td className="no">별개의 요약</td><td className="ours yes">연결되어 stale 표시</td></tr>
            <tr><td>몇 주 후</td><td className="partial">낡았는지 알 수 없음</td><td className="ours yes">최신인지 한눈에</td></tr>
          </tbody>
        </table>
      </section>

      <section className="pullquote">
        <blockquote className="serif">
          요약은 '결론'을 남긴다. 하지만 일을 움직이는 건 이유를 데려온 '결정'이다.
        </blockquote>
        <cite>요약에서 멈추지 말고, 결정을 맥락째 남기세요. 그게 Draftlize.</cite>
      </section>

      <section className="compare" style={{ paddingTop: 64 }}>
        <div className="head">
          <div>
            <span className="label">자주 묻는 질문</span>
            <h2 className="serif">FAQ.</h2>
          </div>
        </div>
        <div style={{ maxWidth: 820 }}>
          {[
            { q: 'PDF를 무료로 요약하는 방법은?', a: '여러 AI 요약 도구가 무료 한도를 제공합니다. Draftlize는 요약 도구가 아니라, 요약한 내용 속 결정을 최신으로 유지하는 카드로 만드는 역할입니다.' },
            { q: 'AI 요약은 정확한가요?', a: '최근 AI 요약은 실용적입니다. 다만 \'왜\'를 자주 생략하므로, 나중에 판단 근거로 쓰려면 이유를 보강해야 합니다.' },
            { q: '요약한 내용을 어떻게 활용하나요?', a: '결론뿐 아니라 그 뒤의 결정을 Draftlize에 카드로 남깁니다. 다른 결정이 그것을 뒤집으면 자동으로 stale 표시되어, 낡은 결론을 알아챌 수 있습니다.' },
          ].map((f, i) => (
            <div key={f.q} style={{ padding: '18px 0', borderTop: i === 0 ? 0 : '1px dashed var(--rule)' }}>
              <h3 className="serif" style={{ fontSize: 18, margin: '0 0 8px', color: 'var(--ink-0)' }}>{f.q}</h3>
              <p style={{ margin: 0, fontSize: 15, lineHeight: 1.85, color: 'var(--ink-1)' }}>{f.a}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="pricing" style={{ paddingTop: 80 }}>
        <div className="head">
          <h2 className="serif">요약한 문서를 결정으로 연결 <em>—— $5 무료로.</em></h2>
        </div>
        <div className="trust-strip">
          <span>신규 가입 시 $5 지급</span>
          <span>사용한 만큼만 결제</span>
          <span>잔액은 만료되지 않음</span>
        </div>
        <div style={{ textAlign: 'center', padding: '40px 24px', border: '1px solid var(--ink-0)', borderTop: 0, background: 'var(--paper-1)' }}>
          <p style={{ fontSize: 16, color: 'var(--ink-1)', margin: '0 0 24px', maxWidth: 640, marginLeft: 'auto', marginRight: 'auto' }}>
            요약 도구는 그대로 쓰세요. 결론 뒤의 결정을 Draftlize에 남기면 이유째 최신으로 유지되고, AI 에이전트도 읽습니다. 다음 판단이 빨라집니다.
          </p>
          <a className="btn-primary" href="/">$5 무료로 시작하기</a>
        </div>
      </section>
    </MarketingShell>
  );
}
